import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { inject, injectable } from 'src/features/ioc';
import { instrumentToString } from 'src/lib/instrumentUtils';
import { debouncedSignal } from 'src/lib/signals/debouncedSignal';
import type { ActiveUserModel } from 'src/models/ActiveUserModel';
import type { TableModel } from 'src/models/TableModel';
import { getTickersForSymbolsQuery } from 'src/queries/table';
import type { MarketDataGroupService } from 'src/services/MarketDataGroupService';
import type { MarketData, MarketDataSubscription } from 'src/services/MarketDataService';
import type { ReactBindings } from 'src/types/bindings';

const MARKET_DATA_ROLES = {
    equity: 'market-data::equity',
    option: 'market-data::options',
} as const;

/**
 * Represents a single instrument's subscription within a group.
 * Tracks subscribers and provides the market data signal for this instrument.
 */
class GroupSubscriptionEntry {
    readonly instrumentKey: string;
    readonly marketData: Signal<MarketData>;
    readonly #subscribers = new Set<string>();
    readonly #onEmpty: () => void;

    constructor(instrumentKey: string, onEmpty: () => void) {
        this.instrumentKey = instrumentKey;
        this.#onEmpty = onEmpty;
        this.marketData = signal<MarketData>({
            bid: undefined,
            ask: undefined,
            last: undefined,
        });
    }

    addSubscriber(subscriberId: string): void {
        this.#subscribers.add(subscriberId);
    }

    removeSubscriber(subscriberId: string): void {
        this.#subscribers.delete(subscriberId);

        // Notify when no more subscribers
        if (this.#subscribers.size === 0) {
            this.#onEmpty();
        }
    }

    get subscriberCount(): number {
        return this.#subscribers.size;
    }
}

// Debounce delay in milliseconds for batching instrument key changes
const INSTRUMENT_KEYS_DEBOUNCE_MS = 300;

/**
 * Manages all subscription entries within a group.
 * Coordinates the shared TableModel and routes market data updates to the correct instruments.
 */
class GroupSubscriptionManager {
    readonly #entries = new Map<string, GroupSubscriptionEntry>();
    readonly #instrumentKeys: Signal<string[]>;
    readonly #debouncedInstrumentKeys: ReadonlySignal<string[]>;
    readonly #debouncedDispose: () => void;
    readonly #tableModel: TableModel;
    readonly #isConnected: ReadonlySignal<boolean>;
    readonly #disposables: DisposableStack;
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;

    constructor(tableModel: TableModel, logger: ReturnType<ReactBindings['Logger']['getSubLogger']>) {
        using stack = new DisposableStack();

        this.#tableModel = tableModel;
        this.#log = logger;

        // Track instruments in this group
        this.#instrumentKeys = signal<string[]>([]);

        // Create a debounced version of instrument keys to batch rapid changes
        const [debouncedKeys, debouncedDispose] = debouncedSignal(this.#instrumentKeys, INSTRUMENT_KEYS_DEBOUNCE_MS);
        this.#debouncedInstrumentKeys = debouncedKeys;
        this.#debouncedDispose = debouncedDispose;
        stack.defer(() => this.#debouncedDispose());

        // Create computed signal for query options based on debounced instruments
        const queryOptionsSignal = computed(() => getTickersForSymbolsQuery(this.#debouncedInstrumentKeys.value));

        // Initialize table model with signal-based query options
        this.#tableModel.init({
            queryOptions: queryOptionsSignal as Signal<any>,
            initBounds: { firstRow: 0, windowSize: 100 },
            initThrottle: 1,
        });

        stack.defer(() => this.#tableModel.destroy());

        // Connection status is shared across all instruments
        this.#isConnected = computed(() => this.#tableModel.connectionStatus.value === 'connected');

        // Set up effect to route market data updates to the correct instruments
        stack.defer(
            effect(() => {
                if (!this.#isConnected.value) return;

                const updateData = this.#tableModel.updateSignal.value?.update;
                if (!updateData) return;

                const columns = this.#tableModel.columnsSignal.value;
                const columnBid = columns.find((col) => col.matchSlug === Slugs.bid);
                const columnAsk = columns.find((col) => col.matchSlug === Slugs.ask);
                const columnLast = columns.find((col) => col.matchSlug === Slugs.last);
                const columnSymbol = columns.find((col) => col.matchSlug === Slugs.symbol);

                if (!columnSymbol) return;

                // Extract symbol from the update to route to correct instrument
                const symbol = updateData[columnSymbol.id] as string | undefined;
                if (!symbol) return;

                // Update market data for this specific instrument
                const entry = this.#entries.get(symbol);
                if (entry) {
                    entry.marketData.value = {
                        bid: columnBid ? (updateData[columnBid.id] as number | undefined) : undefined,
                        ask: columnAsk ? (updateData[columnAsk.id] as number | undefined) : undefined,
                        last: columnLast ? (updateData[columnLast.id] as number | undefined) : undefined,
                    };
                }
            }),
        );

        this.#disposables = stack.move();
    }

    /**
     * Get or create an entry for an instrument, and add a subscriber to it.
     * Returns a disposable subscription.
     */
    addSubscriber(instrumentKey: string, subscriberId: string): MarketDataSubscription {
        // Get or create entry for this instrument
        let entry = this.#entries.get(instrumentKey);

        if (!entry) {
            entry = new GroupSubscriptionEntry(instrumentKey, () => this.#removeInstrument(instrumentKey));
            this.#entries.set(instrumentKey, entry);

            // Update the instrument keys signal (triggers table re-initialization)
            this.#instrumentKeys.value = [...this.#instrumentKeys.value, instrumentKey];

            if (TRACE) {
                this.#log.trace('Added instrument to group', {
                    instrumentKey,
                    totalInstruments: this.#instrumentKeys.value.length,
                    willDebounce: this.#instrumentKeys.value.length !== this.#debouncedInstrumentKeys.value.length,
                });
            }
        }

        // Add subscriber to the entry
        entry.addSubscriber(subscriberId);

        return {
            tableModel: this.#tableModel,
            marketData: entry.marketData,
            isConnected: this.#isConnected,
            [Symbol.dispose]: () => this.#removeSubscriber(instrumentKey, subscriberId),
        };
    }

    #removeSubscriber(instrumentKey: string, subscriberId: string): void {
        const entry = this.#entries.get(instrumentKey);
        if (!entry) return;

        entry.removeSubscriber(subscriberId);
    }

    #removeInstrument(instrumentKey: string): void {
        this.#entries.delete(instrumentKey);

        // Update the instrument keys signal (triggers table re-initialization)
        this.#instrumentKeys.value = this.#instrumentKeys.value.filter((key) => key !== instrumentKey);

        if (TRACE) {
            this.#log.trace('Removed instrument from group', {
                instrumentKey,
                remainingInstruments: this.#instrumentKeys.value.length,
            });
        }
    }

    setDevtoolsName(name: string): void {
        this.#tableModel.setDevtoolsName(name);
    }

    [Symbol.dispose](): void {
        this.#disposables.dispose();
    }
}

@injectable()
export class MarketDataGroupServiceImpl implements MarketDataGroupService {
    readonly #manager: GroupSubscriptionManager;
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    readonly #activeUserModel: ActiveUserModel;

    constructor(
        @inject('TableModel') tableModel: TableModel,
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('ActiveUserModel') activeUserModel: ActiveUserModel,
    ) {
        this.#log = logger.getSubLogger({ name: 'MarketDataGroupService' });
        this.#manager = new GroupSubscriptionManager(tableModel, this.#log);
        this.#activeUserModel = activeUserModel;

        if (TRACE) {
            this.#log.trace('MarketDataGroupService initialized');
        }
    }

    #hasPermission(instrumentType: 'equity' | 'option'): boolean {
        if (this.#activeUserModel.isSuperUserModeEnabled.value) {
            return true;
        }
        return this.#activeUserModel.userRoles.value.includes(MARKET_DATA_ROLES[instrumentType]);
    }

    subscribe(instrument: Instrument): MarketDataSubscription | null {
        if (!this.#hasPermission(instrument.type)) {
            if (TRACE) {
                this.#log.trace('Market data permission denied', { instrumentType: instrument.type });
            }
            return null;
        }

        const instrumentKey = this.#getInstrumentKey(instrument);
        const subscriberId = crypto.randomUUID();

        if (TRACE) {
            this.#log.trace('Adding subscriber to group', { instrumentKey, subscriberId });
        }

        return this.#manager.addSubscriber(instrumentKey, subscriberId);
    }

    setDevtoolsName(name: string): void {
        this.#manager.setDevtoolsName(name);
    }

    #getInstrumentKey(instrument: Instrument): string {
        return instrumentToString(instrument);
    }

    [Symbol.dispose](): void {
        if (TRACE) {
            this.#log.trace('Disposing MarketDataGroupService');
        }

        this.#manager[Symbol.dispose]();
    }
}
